/**
 * API client.
 *
 * Resolution order:
 *   1. `VITE_API_BASE_URL` build-time env (e.g. for staging / preview deploys
 *      that need a fully-qualified URL).
 *   2. Production: a relative `/api/v1` path. Caddy in the SPA service
 *      reverse-proxies `/api/*` to the backend over Railway's private
 *      network, preserving the Host header so the tenant middleware
 *      resolves correctly. Same-origin = no CORS.
 *   3. Dev: `http://{currentHostname}:8100/api/v1` so `lighthouse.localhost:5273`
 *      reaches the docker-exposed FastAPI at `lighthouse.localhost:8100`.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

function resolveBaseUrl(): string {
  const fromEnv = import.meta.env?.VITE_API_BASE_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/+$/, "");
  if (import.meta.env?.PROD) return "/api/v1";
  const host = typeof window !== "undefined" ? window.location.hostname : "localhost";
  return `http://${host}:8100/api/v1`;
}

export const API_BASE_URL = resolveBaseUrl();

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
});

const TOKEN_KEY = "m2s.access_token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.headers) {
    config.headers["X-Request-Id"] =
      config.headers["X-Request-Id"] ?? crypto.randomUUID();
  }
  return config;
});

/**
 * Pull a human-readable string out of any axios error. Handles:
 *   - FastAPI 4xx with {detail: "string"}    → that string
 *   - FastAPI 422 with {detail: [{msg, loc, ...}, ...]} → joined `loc.field: msg`
 *   - network/timeout (no response)          → "Network error."
 *   - anything else                          → fallback you provide
 *
 * Always returns a string so it can safely be put in React state and rendered.
 */
/**
 * Trigger a browser download of a server-generated binary (xlsx/pdf/...).
 *
 * The endpoint streams the bytes with a Content-Disposition header; we fetch
 * as `responseType: 'blob'`, build an object URL, and click a hidden <a> so
 * the user's browser names the file from the header.
 *
 * `fallbackName` is only used if the response doesn't include a filename
 * suggestion — the server normally sets one.
 */
export async function downloadBlob(
  url: string,
  fallbackName: string,
): Promise<void> {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = resp.data as Blob;

  // Try to honour the server's filename hint.
  let filename = fallbackName;
  const disposition = (resp.headers as Record<string, string | undefined>)[
    "content-disposition"
  ];
  if (disposition) {
    const match = /filename\*?=(?:UTF-\d+''|)?["']?([^"';]+)["']?/i.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);
  }

  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before we revoke.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}


export function extractErrorMessage(err: unknown, fallback = "Something went wrong."): string {
  const e = err as {
    message?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const msg = (d as { msg?: string })?.msg;
        const loc = (d as { loc?: unknown[] })?.loc;
        const field = Array.isArray(loc) ? loc.filter((p) => p !== "body").join(".") : "";
        return field && msg ? `${field}: ${msg}` : msg ?? JSON.stringify(d);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  if (!e?.response) return "Network error — check your connection.";
  return e.message ?? fallback;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error?.response?.status === 401) {
      setToken(null);
      // Lazy-load the auth store to avoid circular imports.
      try {
        const { useAuthStore } = await import("@/store/auth");
        useAuthStore.getState().clear();
      } catch {
        /* noop */
      }
      if (window.location.pathname !== "/login") {
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.assign(`/login?next=${next}`);
      }
    }
    return Promise.reject(error);
  },
);
